import { useQuery } from '@tanstack/react-query';
import type { Entitlements } from '@plerionhq/types-rbac';

import { apiClient } from './api-client';

export const ENTITLEMENTS_QUERY_KEY = ['entitlements'] as const;

/**
 * The current user's resolved RBAC entitlements for their active tenant,
 * served by service-rbac (`GET /api/base/entitlements`). Built-in and custom
 * users resolve to the same shape. Consumed by useAccessControl / AccessControl.
 */
export const useEntitlements = () =>
  useQuery({
    queryKey: ENTITLEMENTS_QUERY_KEY,
    queryFn: async (): Promise<Entitlements> => {
      const response = await apiClient.get<Entitlements>(
        '/api/base/entitlements',
      );
      return response.data;
    },
    // Treated as session-stable: fetched once and not refetched, so gating is
    // predictable and cheap. Set here rather than relying on the host's
    // QueryClient defaults, which differ between the shell and ciem. A role
    // change therefore shows up on reload; enforcement is server-side either
    // way, so the window only affects what the UI offers, never what it allows.
    staleTime: Infinity,
    refetchOnWindowFocus: false,
    // A failed fetch stays failed for the session too. Otherwise every mount
    // restarts it, and a caller that always fails (the print renderer has no
    // user record) never stops loading.
    retryOnMount: false,
  });
